import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  MapPin, Monitor, Smartphone, LogIn, LogOut, Eye, Plus, Pencil, Trash2, Download,
  Users2, Clock, Filter, X,
} from "lucide-react";
import { formatDMYTime } from "@/lib/dates";

const ACTION_META = {
  login: { label: "Ingreso", tone: "text-emerald-500", Icon: LogIn },
  logout: { label: "Salida", tone: "text-muted-foreground", Icon: LogOut },
  view: { label: "Vio", tone: "text-sky-500", Icon: Eye },
  create: { label: "Creó", tone: "text-emerald-500", Icon: Plus },
  update: { label: "Modificó", tone: "text-amber-500", Icon: Pencil },
  delete: { label: "Eliminó", tone: "text-rose-500", Icon: Trash2 },
  export: { label: "Exportó", tone: "text-violet-500", Icon: Download },
};

const RESOURCE_LABEL = {
  auth: "Sesión", property: "Propiedad", transaction: "Movimiento",
  user: "Usuario", alert: "Alerta", document: "Documento",
  tenant: "Inquilino", contract: "Contrato", payment: "Pago",
  provider: "Proveedor", maintenance: "Mantenimiento",
  recurring_expense: "Cargo recurrente", report: "Reporte", backup: "Backup",
};

function DeviceBadge({ device, browser, os }) {
  if (!device && !browser && !os) return null;
  const IconComp = device && (device.includes("iPhone") || device.includes("Android") || device === "Mobile")
    ? Smartphone : Monitor;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground border border-border/60 rounded-full px-1.5 py-0.5">
      <IconComp className="w-3 h-3" />
      {[browser, os].filter(Boolean).join(" · ")}
    </span>
  );
}

function LocationBadge({ city, country, country_code, ip }) {
  const loc = [city, country].filter(Boolean).join(", ");
  return (
    <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
      <MapPin className="w-3 h-3" />
      {loc || ip || "-"} {country_code && `(${country_code})`}
    </span>
  );
}

export default function Audit() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState([]);
  const [users, setUsers] = useState([]);
  const today = new Date().toISOString().slice(0, 10);
  const [filters, setFilters] = useState({ user_email: "all", action: "all", resource: "all", start_date: today, end_date: today });
  const [loading, setLoading] = useState(false);

  const loadAudit = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.user_email !== "all") params.append("user_email", filters.user_email);
    if (filters.action !== "all") params.append("action", filters.action);
    if (filters.resource !== "all") params.append("resource", filters.resource);
    if (filters.start_date) params.append("start_date", filters.start_date);
    if (filters.end_date) params.append("end_date", filters.end_date);
    params.append("limit", "500");
    try {
      const [a, s, u] = await Promise.all([
        api.get(`/audit?${params.toString()}`),
        api.get("/audit/summary?days=30"),
        api.get("/users/directory"),
      ]);
      setItems(a.data);
      setSummary(s.data);
      setUsers(u.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAudit(); /* eslint-disable-next-line */ }, [filters]);

  const clearFilters = () => {
    const t = new Date().toISOString().slice(0, 10);
    setFilters({ user_email: "all", action: "all", resource: "all", start_date: t, end_date: t });
  };
  const activeFilters = Object.entries(filters).filter(([k, v]) => {
    if (v === "all" || !v) return false;
    // Don't count the default today→today range as an "active" filter badge
    if ((k === "start_date" || k === "end_date") && v === today) return false;
    return true;
  }).length;

  const totalActivity = useMemo(() => summary.reduce((acc, u) => acc + Object.values(u.counts).reduce((a, b) => a + b, 0), 0), [summary]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Auditoría</h1>
        <div className="text-sm text-muted-foreground mt-1">
          Mostrando {items.length} registros de hoy · {totalActivity} eventos totales en los últimos 30 días. Ajustá las fechas para ver historial anterior.
        </div>
      </div>

      {/* Per-user summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {summary.map((u) => (
          <div key={u.user_email} className="rounded-xl border border-border/60 bg-card p-4 space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-secondary grid place-items-center text-xs font-semibold shrink-0">
                {(u.user_email || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{u.user_email}</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Último: {formatDMYTime(u.last_activity)}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(u.counts).map(([action, count]) => {
                const meta = ACTION_META[action] || { label: action, tone: "text-muted-foreground" };
                return (
                  <span key={action} className={`text-[10px] uppercase tracking-widest border border-border/60 rounded-full px-2 py-0.5 ${meta.tone}`}>
                    {meta.label} {count}
                  </span>
                );
              })}
            </div>
            {u.last_login && (
              <div className="pt-2 border-t border-border/60 space-y-1">
                <div className="flex items-center gap-1.5">
                  <LocationBadge city={u.last_login.city} country={u.last_login.country} country_code={u.last_login.country_code} ip={u.last_login.ip} />
                </div>
                <DeviceBadge device={u.last_login.device} browser={u.last_login.browser} os={u.last_login.os} />
              </div>
            )}
          </div>
        ))}
        {!summary.length && (
          <div className="col-span-full text-center py-8 text-muted-foreground text-sm">
            Sin actividad en los últimos 30 días.
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-border/60 bg-card p-4 flex flex-wrap gap-3 items-end">
        <div className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-muted-foreground">
          <Filter className="w-3 h-3" /> Filtros{activeFilters > 0 && ` (${activeFilters})`}
        </div>
        <div className="min-w-[180px] flex-1">
          <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Usuario</Label>
          <Select value={filters.user_email} onValueChange={(v) => setFilters({ ...filters, user_email: v })}>
            <SelectTrigger data-testid="audit-filter-user"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {users.map((u) => <SelectItem key={u.id} value={u.email}>{u.name} — {u.email}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[140px]">
          <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Acción</Label>
          <Select value={filters.action} onValueChange={(v) => setFilters({ ...filters, action: v })}>
            <SelectTrigger data-testid="audit-filter-action"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {Object.entries(ACTION_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[140px]">
          <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Recurso</Label>
          <Select value={filters.resource} onValueChange={(v) => setFilters({ ...filters, resource: v })}>
            <SelectTrigger data-testid="audit-filter-resource"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {Object.entries(RESOURCE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Desde</Label>
          <Input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Hasta</Label>
          <Input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} />
        </div>
        {activeFilters > 0 && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="w-4 h-4 mr-1" /> Limpiar
          </Button>
        )}
      </div>

      {/* Log table */}
      <div className="rounded-xl border border-border/60 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60">
            <tr className="text-left">
              <th className="px-3 py-3 font-medium">Cuándo</th>
              <th className="px-3 py-3 font-medium">Usuario</th>
              <th className="px-3 py-3 font-medium">Acción</th>
              <th className="px-3 py-3 font-medium">Recurso</th>
              <th className="px-3 py-3 font-medium">Detalle</th>
              <th className="px-3 py-3 font-medium">Contexto</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => {
              const meta = ACTION_META[a.action] || { label: a.action, tone: "text-muted-foreground", Icon: Clock };
              const Icon = meta.Icon;
              const details = a.details || {};
              const isLogin = a.action === "login";
              const detailKeys = Object.keys(details).filter((k) => !["ip", "city", "region", "country", "country_code", "org", "device", "browser", "os"].includes(k));
              return (
                <tr key={a.id} className="border-t border-border/50 hover:bg-secondary/40 transition-colors align-top">
                  <td className="px-3 py-2.5 num text-xs whitespace-nowrap">{formatDMYTime(a.created_at)}</td>
                  <td className="px-3 py-2.5">
                    <div className="text-sm">{a.user_email || "—"}</div>
                  </td>
                  <td className={`px-3 py-2.5 ${meta.tone}`}>
                    <span className="inline-flex items-center gap-1"><Icon className="w-3.5 h-3.5" />{meta.label}</span>
                  </td>
                  <td className="px-3 py-2.5 text-muted-foreground">
                    {RESOURCE_LABEL[a.resource] || a.resource}
                    {a.resource_id && <div className="text-[10px] font-mono opacity-60">{a.resource_id.slice(0, 8)}…</div>}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground max-w-md">
                    {detailKeys.length ? (
                      <div className="space-y-0.5">
                        {detailKeys.slice(0, 4).map((k) => (
                          <div key={k} className="truncate">
                            <span className="text-foreground/80">{k}:</span> {String(details[k]).slice(0, 60)}
                          </div>
                        ))}
                      </div>
                    ) : <span className="opacity-60">—</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    {isLogin ? (
                      <div className="space-y-1">
                        <LocationBadge city={details.city} country={details.country} country_code={details.country_code} ip={details.ip} />
                        <DeviceBadge device={details.device} browser={details.browser} os={details.os} />
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground opacity-60">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {!items.length && !loading && (
              <tr><td colSpan={6} className="px-3 py-10 text-center text-muted-foreground">Sin registros con estos filtros.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
